import { createSelector } from '@reduxjs/toolkit';
import { InventoryItem, selectActiveInventory } from './inventorySlice';
import { PantryLocation, PANTRY_LOCATIONS, inferLocation } from '../utils/pantryLocation';
import {
  findDuplicateGroupsLearned,
  findMergeCandidates,
  type Learning,
} from '../utils/pantryDuplicates';
import type { RootState } from './store';
import {
  FreshnessState, freshnessOf, daysUntilExpiry, isCookCandidate, sortCookList,
} from '../services/pantryFreshnessService';

export interface PantryItemView {
  item: InventoryItem;
  state: FreshnessState;
  days: number | null;
}

export interface PantrySnapshot {
  all: PantryItemView[];
  byLocation: Record<PantryLocation, PantryItemView[]>;
  urgentCountByLocation: Record<PantryLocation, number>;
  cookList: PantryItemView[];
  /** Freshness review: "is this still good?" */
  reviewList: PantryItemView[];
  /**
   * Sorting review: "what is this, and where does it live?" — rows whose zone
   * was never actually determined. Deliberately separate from `reviewList`;
   * conflating them would put a mystery item in the same queue as a wilting one.
   */
  unsortedList: PantryItemView[];
  totalCount: number;
}

const STATE_ORDER: FreshnessState[] = ['urgent', 'review', 'soon', 'fresh', 'stable'];

/** Pure builder so tests don't need a store. `today` injected for determinism. */
export function buildPantrySnapshot(items: InventoryItem[], today: Date = new Date()): PantrySnapshot {
  const all: PantryItemView[] = items.map((item) => ({
    item,
    state: freshnessOf(item, today),
    days: daysUntilExpiry(item, today),
  }));

  const byLocation = {} as Record<PantryLocation, PantryItemView[]>;
  const urgentCountByLocation = {} as Record<PantryLocation, number>;
  for (const loc of PANTRY_LOCATIONS) {
    const views = all
      .filter((v) => (v.item.location ?? inferLocation(v.item.category, v.item.name)) === loc)
      .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));
    byLocation[loc] = views;
    urgentCountByLocation[loc] = views.filter((v) => v.state === 'urgent' || v.state === 'review').length;
  }

  const cookItems = sortCookList(all.filter((v) => isCookCandidate(v.item, today)).map((v) => v.item), today);
  const viewOf = new Map(all.map((v) => [v.item.id, v]));
  const cookList = cookItems.map((i) => viewOf.get(i.id)!);
  const reviewList = all.filter((v) => v.state === 'review');
  const unsortedList = all.filter((v) => v.item.needsSorting === true);

  return {
    all, byLocation, urgentCountByLocation, cookList, reviewList, unsortedList,
    totalCount: items.length,
  };
}

export const selectPantrySnapshot = createSelector(
  [selectActiveInventory],
  (items) => buildPantrySnapshot(items),
);

/** What the user has taught the matcher, in the shape the matcher wants. */
export const selectPantryLearning = (state: RootState): Learning => ({
  aliases: state.pantryLearning?.aliases ?? [],
  distinct: state.pantryLearning?.distinct ?? [],
});

const EMPTY_COVERAGE: never[] = [];

/**
 * What the user has taught the coverage matcher ("this pantry row covers that
 * ingredient"), in the shape `deriveIngredientStatuses` wants.
 *
 * Memoized (#668): the recipe-detail screen used to build this object inside
 * `useSelector`, so every dispatched action anywhere handed React a new object
 * and the whole screen re-rendered, re-running the pantry pass each time.
 */
export const selectLearnedCoverage = createSelector(
  [
    (state: RootState) => state.pantryLearning?.covers ?? EMPTY_COVERAGE,
    (state: RootState) => state.pantryLearning?.notCovers ?? EMPTY_COVERAGE,
  ],
  (covers, notCovers) => ({ covers, notCovers }),
);

/**
 * Groups we are confident are the same food, with the user's confirmations and
 * rejections applied. Pre-checked in the review inbox.
 */
export const selectCertainDuplicateGroups = createSelector(
  [selectActiveInventory, selectPantryLearning],
  (items, learning) => findDuplicateGroupsLearned(items, learning),
);

/**
 * Groups that merely look alike. Presented unchecked, as a question — the
 * answer, either way, is remembered.
 */
export const selectMergeCandidates = createSelector(
  [selectActiveInventory, selectPantryLearning],
  (items, learning) => findMergeCandidates(items, learning),
);

/** Rows whose zone was never determined; they need the user to place them. */
export const selectUnsortedItems = createSelector(
  [selectActiveInventory],
  (items) => items.filter((item) => item.needsSorting === true),
);

/** Everything the review inbox has to offer, for the entry-point badge. */
export const selectTidyCount = createSelector(
  [selectCertainDuplicateGroups, selectMergeCandidates, selectUnsortedItems],
  (certain, candidates, unsorted) => certain.length + candidates.length + unsorted.length,
);
